import { KeyboardEvent } from "react";

import Link from "@whoppah/next/link";
import { useRouter } from "next/router";

import { ChevronRight as ChevronRightIcon } from "@/assets/icons/24/ChevronRight";
import { useLocaleLinks } from "@/common/hooks/intl.hooks";
import { setLocaleCookie } from "@/common/utils/cookie.utils";
import { cn } from "@/common/utils/css.utils";
import { getLanguageName } from "@/common/utils/intl.utils";
import { cleanSearchParamsFromUrl } from "@/common/utils/router.utils";
import { useHeaderLanguageSwitcherContext } from "@/components/Header/HeaderLanguageSwitcherContext";
import { MegaMenuMobileActionProps } from "@/components/mega-menu/mobile/MegaMenuMobileActionDelegator";
import { MegaMenuMobileBack } from "@/components/mega-menu/mobile/MegaMenuMobileBack";
import { MegaMenuMobileClose } from "@/components/mega-menu/mobile/MegaMenuMobileClose";
import { Flavor } from "@/ui/typography/Flavor";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTrigger,
} from "@radix-ui/react-dialog";

export const MegaMenuMobileLanguageAction = ({
  onClick,
  children,
  className,
  ...props
}: MegaMenuMobileActionProps) => {
  const router = useRouter();
  const { urls } = useHeaderLanguageSwitcherContext();
  const { links } = useLocaleLinks(urls);

  const handleKeyDown = (evt: KeyboardEvent<HTMLAnchorElement>) => {
    if (evt.key !== "Enter") {
      return;
    }

    onClick?.();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          {...props}
          className={cn("flex w-full items-center text-left", className)}
        >
          <Flavor className="flex-1">{children}</Flavor>
          <ChevronRightIcon />
        </button>
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="fixed inset-0 z-[1000] flex flex-col space-y-6 bg-white p-4">
          <div className="flex items-center space-x-2 border-b border-b-grey-300 pb-4">
            <MegaMenuMobileBack />
            <span className="flex-1">{children}</span>
            <MegaMenuMobileClose onClick={onClick} />
          </div>
          <div className="flex flex-col space-y-6 overflow-y-auto overflow-x-hidden">
            <ul className="flex flex-col space-y-4">
              {links.map(({ locale, path, sourceLocale }) => (
                <li
                  key={locale}
                  className="font-title text-sm hover:text-attention-500"
                >
                  <Link
                    href={cleanSearchParamsFromUrl(path)}
                    locale={locale}
                    sourceLocale={sourceLocale}
                    prefetch={false}
                    className={
                      router.locale === locale
                        ? "font-bold text-attention-500"
                        : undefined
                    }
                    lang={locale}
                    hrefLang={locale}
                    role="link"
                    tabIndex={0}
                    onClick={() => {
                      setLocaleCookie(locale);
                      onClick?.();
                    }}
                    onKeyDown={handleKeyDown}
                  >
                    <Flavor className="capitalize">
                      {getLanguageName(locale)}
                    </Flavor>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
